/**
 * Tritan PDP 2D Compositor — unified tape-measure model (v3, 2026-07-23)
 * Sole writer of width/aspect-ratio on #pdp-dynamic-art-wrapper.
 * Canvas width renders as an exact fraction of the room wall:
 *   width% = (canvas inches / ROOM_WIDTH_INCHES) * 100
 * so size differences between variants are true to a tape measure.
 *
 * v3.1 (2026-07-24): size-aware camera pull-back. The zoom that used to be a
 * static CSS scale now varies with the selected size — tight on a small canvas,
 * pulled back for a big one — via the --mockup-zoom custom property on the
 * compositor. The tape-measure math below is UNTOUCHED: the room and the art
 * scale together, so the art-to-sofa ratio is exact at every zoom.
 */
document.addEventListener('DOMContentLoaded', function () {
  var artWrapper = document.getElementById('pdp-dynamic-art-wrapper');
  if (!artWrapper) return;

  var ROOM_WIDTH_INCHES = 156; // true real-world width of the full room photo. Anchored to the
                               // sofa: it spans ~55% of the frame and reads as a standard 86″
                               // 3-seat sofa (86 / 0.55 ≈ 156). The square photo shows full-width
                               // (object-fit:cover into a 1:1 stage), so this IS the whole wall.
                               // Was 80 = ~2× too large / "fabricated" (Kyle, 2026-07-23).
  var MAX_WIDTH_PCT = 80;     // hard cap so art never touches the container edges (won't bind: 40″ → 25.6%)

  // ── Camera pull-back (2026-07-24, Kyle: "start super zoomed for the 8x10 and
  // pull the camera back as the size grows"). Cheap variant: same 1024px room,
  // zoom stays inside the resolution budget already calibrated on live pixels.
  // max values are LOCKED CEILINGS (2.5 mobile / 1.9 desktop — the room photo
  // softens beyond them; see the CSS comment block in alpine-product-main.liquid).
  // Zoom keys on the LARGER dimension so a 24×36 vertical and a 36×24 horizontal
  // sit at the same camera distance. Log-interpolated between the anchors and
  // clamped, so out-of-range sizes can never exceed the ceiling.
  var ZOOM = {
    mobile:  { max: 2.5,  min: 1.6  },
    desktop: { max: 1.9,  min: 1.35 }
  };
  var ZOOM_DIM_MIN = 10, ZOOM_DIM_MAX = 60; // inches: smallest/largest long side sold
  var mqMobile = window.matchMedia('(max-width: 749px)');
  var compositor = document.querySelector('.pdp-mockup-compositor');

  function applyZoom() {
    if (!compositor || !lastSize) return;
    var range = mqMobile.matches ? ZOOM.mobile : ZOOM.desktop;
    var dim = Math.max(lastSize.w, lastSize.h);
    var k = Math.log(range.max / range.min) / Math.log(ZOOM_DIM_MAX / ZOOM_DIM_MIN);
    var z = range.max * Math.pow(dim / ZOOM_DIM_MIN, -k);
    z = Math.max(range.min, Math.min(range.max, z));
    compositor.style.setProperty('--mockup-zoom', z.toFixed(3));
  }

  if (mqMobile.addEventListener) {
    mqMobile.addEventListener('change', applyZoom);
  }

  var sizeLabel = document.getElementById('pdp-mockup-size-label');
  var lastSize = null;

  function parseSizeFromTitle(rawTitle) {
    if (!rawTitle) return null;
    var t = String(rawTitle)
      .replace(/[″“”]/g, '"')  // ″ “ ” → "
      .replace(/[′‘’]/g, "'")  // ′ ‘ ’ → '
      .replace(/ /g, ' ');
    if (/digital|vault/i.test(t)) return null; // non-physical variant
    var cleaned = t.replace(/["']/g, ' ');
    var re = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/gi;
    var m, a, b;
    while ((m = re.exec(cleaned)) !== null) {
      a = parseFloat(m[1]); b = parseFloat(m[2]);
      // sanity bounds keep depth fragments like "0.75 / 8" from ever matching
      if (a >= 4 && a <= 80 && b >= 4 && b <= 80) {
        var w = a, h = b;
        if (/vertical|portrait/i.test(t) && w > h) { w = b; h = a; }
        if (/horizontal|landscape/i.test(t) && w < h) { w = b; h = a; }
        return { w: w, h: h };
      }
    }
    return null;
  }

  function applySize(size) {
    var widthPct = Math.min((size.w / ROOM_WIDTH_INCHES) * 100, MAX_WIDTH_PCT);
    artWrapper.style.width = widthPct.toFixed(2) + '%';
    artWrapper.style.aspectRatio = size.w + ' / ' + size.h;
    if (sizeLabel) sizeLabel.textContent = 'Shown to scale: ' + size.w + '″ × ' + size.h + '″';
    lastSize = size;
    applyZoom();
  }

  function updateMockupSize(title) {
    var size = parseSizeFromTitle(title);
    if (size) applySize(size);
    // else: digital/unparseable → keep last physical size (no reset, no fabrication)
  }

  // --- Init from the CURRENT variant (server-rendered input honors ?variant= URLs) ---
  var titles = {};
  var titlesEl = document.getElementById('pdp-variant-titles');
  if (titlesEl) { try { titles = JSON.parse(titlesEl.textContent); } catch (e) {} }

  var variantInput = document.getElementById('pdp-variant-input');
  if (variantInput && titles[variantInput.value]) {
    updateMockupSize(titles[variantInput.value]);
  } else {
    var keys = Object.keys(titles);
    if (keys.length) updateMockupSize(titles[keys[0]]);
    // if even that fails: CSS default (20% width, 4/5 aspect — a 16×20 on the 80″ wall) stands
  }

  // --- Variant changes ---
  document.addEventListener('alpine:variant-change', function (e) {
    if (!e.detail || !e.detail.variant) return;
    var v = e.detail.variant;
    updateMockupSize(titles[v.id] || v.title || v.name);
  });
});
